import { $isCodeHighlightNode } from '@lexical/code'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import { $getSelection, $isParagraphNode, $isRangeSelection, $isTextNode, FORMAT_TEXT_COMMAND } from 'lexical'
import { useCallback, useEffect, useState } from 'react'
import React, { FC } from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Popper } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import AddLinkIcon from '@mui/icons-material/AddLink'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'

import { getSelectedNode } from '../../utils/getSelectedNode'
import { useCustomTranslation } from '../../../../Hooks/useCustomTranslation'
import { EMPTY_LINK } from './const'

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(1),
    background: theme.shape.markdownEditor.background,
    boxShadow: theme.shape.markdownEditor.shadowPopover,
    borderRadius: theme.shape.borderRadius,
  },
  button: {
    padding: theme.spacing(1),
    minWidth: 0,
    minHeight: 0,
    marginRight: theme.spacing(1),
    svg: {
      height: 'auto',
    },
  },
  buttonIcon: {
    width: 16,
  },
}))

/**
 * Popover for changing format for selected text
 */
export const FloatingTextFormatToolbarPlugin: FC = () => {
  const [editor] = useLexicalComposerContext()
  const { t } = useCustomTranslation()

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [isLink, setIsLink] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const { classes } = useStyles()

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return
      }
      const selection = $getSelection()
      const nativeSelection = window.getSelection()
      const rootElement = editor.getRootElement()

      if (nativeSelection !== null && (!$isRangeSelection(selection) || rootElement === null || !rootElement.contains(nativeSelection.anchorNode))) {
        setAnchor(null)
        return
      }

      if (!$isRangeSelection(selection)) {
        return
      }

      const node = getSelectedNode(selection)

      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))

      // Update links
      const parent = node.getParent()
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== '') {
        const selectedAnchorElement = editor.getElementByKey(selection.anchor.key)

        if ($isTextNode(node) || $isParagraphNode(node)) {
          setAnchor(selectedAnchorElement)
        } else {
          setAnchor(null)
        }
      } else {
        setAnchor(null)
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '')
      if (!selection.isCollapsed() && rawTextContent === '') {
        setAnchor(null)
        return
      }
    })
  }, [editor])

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, EMPTY_LINK)
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink])

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup)
    return () => {
      document.removeEventListener('selectionchange', updatePopup)
    }
  }, [updatePopup])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup()
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setAnchor(null)
        }
      })
    )
  }, [editor, updatePopup])

  return (
    <Popper
      open={Boolean(anchor)}
      anchorEl={anchor}
      popperOptions={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
      placement="right"
      sx={{
        zIndex: 9999,
      }}
      /** TODO add missing values */
      // placeholder={undefined}
      // onPointerEnterCapture={undefined}
      // onPointerLeaveCapture={undefined}
    >
      <Box className={classes.container}>
        {editor.isEditable() && (
          <>
            <ToggleButtonGroup value={isBold}>
              <ToggleButton
                onClick={() => {
                  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
                }}
                aria-label={t('shell.markdown-editor.format-text-as-bold')}
                title={t('shell.markdown-editor.bold')}
                className={classes.button}
                value
              >
                <FormatBoldIcon className={classes.buttonIcon} />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={isItalic}>
              <ToggleButton
                onClick={() => {
                  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
                }}
                aria-label={t('shell.markdown-editor.format-text-as-italics')}
                title={t('shell.markdown-editor.italic')}
                className={classes.button}
                value
              >
                <FormatItalicIcon className={classes.buttonIcon} />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={isStrikethrough}>
              <ToggleButton
                onClick={() => {
                  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
                }}
                aria-label={t('shell.markdown-editor.format-with-strikethrough')}
                title={t('shell.markdown-editor.strikethrough')}
                className={classes.button}
                value
              >
                <StrikethroughSIcon className={classes.buttonIcon} />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={isLink}>
              <ToggleButton
                onClick={insertLink}
                aria-label={t('shell.markdown-editor.insert-link')}
                title={t('shell.markdown-editor.link')}
                sx={{
                  mr: 0,
                }}
                className={classes.button}
                value
              >
                <AddLinkIcon className={classes.buttonIcon} />
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      </Box>
    </Popper>
  )
}
